// src/components/operationsAssociate/ReturnRequestReviewModal.js
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from "moment";

const API_URL = "https://ims.holdthechild.net";

export default function ReturnRequestReviewModal({ request, closeModal }) {
    const [status, setStatus] = useState(''); // "Accepted" or "Rejected"
    const [notes, setNotes] = useState(request.notes || '');
    const [loading, setLoading] = useState(false);

    const handleReview = async (reviewStatus) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error("Authentication token not found.");

            await axios.put(`${API_URL}/api/return-requests/${request._id}/review`, {
                status: reviewStatus,
                notes: notes,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            toast.success(`Asset return request ${reviewStatus.toLowerCase()} successfully!`);
            closeModal(); // Close modal and trigger parent to refresh requests
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || `Failed to ${reviewStatus.toLowerCase()} return request.`;
            toast.error(`Error: ${errorMessage} 😥`);
            console.error("Review request error:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
                    <h2 className="text-2xl font-bold text-orange-600">Review Return Request</h2>
                    <button type="button" onClick={closeModal} className="text-3xl text-gray-400 hover:text-red-500 font-light">&times;</button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mb-6">
                    <div>
                        <p className="font-medium text-gray-700">Asset Name:</p>
                        <p className="p-2 bg-gray-100 rounded">{request.asset?.name || "N/A"}</p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-700">Serial Number:</p>
                        <p className="p-2 bg-gray-100 rounded">{request.asset?.serialNumber || "N/A"}</p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-700">Requested By:</p>
                        <p className="p-2 bg-gray-100 rounded">{request.requestedBy ? `${request.requestedBy.firstName} ${request.requestedBy.lastName}` : "N/A"}</p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-700">Reason for Return:</p>
                        <p className="p-2 bg-gray-100 rounded">{request.reason}</p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-700">Request Date:</p>
                        <p className="p-2 bg-gray-100 rounded">{moment(request.requestDate).format("YYYY-MM-DD HH:mm")}</p>
                    </div>
                    <div>
                        <p className="font-medium text-gray-700">Current Status:</p>
                        <p className={`p-2 rounded font-semibold ${request.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : request.status === 'Accepted' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {request.status}
                        </p>
                    </div>
                    {request.reviewedBy && (
                        <>
                            <div>
                                <p className="font-medium text-gray-700">Reviewed By:</p>
                                <p className="p-2 bg-gray-100 rounded">{request.reviewedBy ? `${request.reviewedBy.firstName} ${request.reviewedBy.lastName}` : "N/A"}</p>
                            </div>
                            <div>
                                <p className="font-medium text-gray-700">Review Date:</p>
                                <p className="p-2 bg-gray-100 rounded">{moment(request.reviewDate).format("YYYY-MM-DD HH:mm")}</p>
                            </div>
                        </>
                    )}
                </div>

                <div className="mb-4">
                    <label htmlFor="notes" className="block text-gray-700 text-sm font-bold mb-2">
                        Operations Associate Notes:
                    </label>
                    <textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows="4"
                        className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Add any relevant notes about the review process..."
                        disabled={request.status !== "Pending" || loading}
                    ></textarea>
                </div>

                <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={closeModal}
                        disabled={loading}
                        className="px-5 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                    >
                        Close
                    </button>
                    {request.status === "Pending" && (
                        <>
                            <button
                                type="button"
                                onClick={() => handleReview("Rejected")}
                                disabled={loading}
                                className="bg-orange-100 text-red-600 px-6 py-2 rounded-lg border border-orange-300 hover:bg-orange-200 transition font-semibold disabled:opacity-50"
                            >
                                {loading ? "Rejecting..." : "Reject Return"}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleReview("Accepted")}
                                disabled={loading}
                                className="bg-orange-100 text-green-700 px-6 py-2 rounded-lg border border-orange-300 hover:bg-orange-200 transition font-semibold disabled:opacity-50"
                            >
                                {loading ? "Accepting..." : "Accept Return"}
                            </button>

                        </>
                    )}
                </div>
            </div>
        </div>
    );
}